/**
 * The drawer's Usage group (ADR 0057 decision 2): how full the context window is, what the
 * prompt cache hits and saves, and whether it is still warm. Read from Session.ext.usage (the
 * agents take it from the transcript); warmth counts down here, every 15 seconds. Below the
 * facts, one quiet line compares continuing with starting fresh (ADR 0062 decision 2a).
 */
import { useState } from 'react';
import type { ContinuationCost, ContinuationOffer, ModelPrice, Session, SessionLiveUsage } from '@orgus/protocol';
import { CONTINUATION_DEFAULTS, agentKindOf, cacheHitRate, cacheSavedUsd, cacheWarmth, continuationForUsage, ratesForKey, sessionLiveUsage } from '@orgus/protocol';
import type { CacheWarmth } from '@orgus/protocol';
import { Flame, RotateCcw, Snowflake } from '@orgus/ui/icons';
import { FactGrid } from '../detail/FactGrid.tsx';
import { Button, ErrorState, Gauge, Tooltip, formatCompact, formatDuration, formatInt, formatMoney, formatPercent, toast, useNow } from '@orgus/ui';
import { useMe } from './api.ts';
import { continueFresh, useContinuationOffer } from './continuation.ts';
import { useCosts, useSessionCost } from '../costs/useCosts.ts';
import { PARTIAL_HINT } from '../sessions/counts.ts';

const MIN = 60_000;
/** A context this full reads as attention: Claude Code compacts soon. */
const CONTEXT_WARN = 0.8;

/** "Warm, about 4m left", "Warm, under a minute left", "Cold". */
export function warmthText(w: CacheWarmth): string {
  if (w.state === 'cold') return 'Cold';
  if (w.state === 'unknown') return 'No request yet';
  return w.leftMs < MIN ? 'Warm, under a minute left' : `Warm, about ${formatDuration(Math.round(w.leftMs / MIN) * MIN)} left`;
}

/** "142K of 200K, 71%", or "142K used" when the window is unknown. */
export function contextText(tokens: number, window: number | null): string {
  return window ? `${formatCompact(tokens)} of ${formatCompact(window)}, ${formatPercent(tokens / window)}` : `${formatCompact(tokens)} used`;
}

/** "Continue about $7.35 for ~20 requests; fresh about $2.57". */
export function continuationText(c: Pick<ContinuationCost, 'continue' | 'fresh' | 'remainingRequests'>): string {
  const n = c.remainingRequests;
  return `Continue about ${formatMoney(c.continue)} for ~${formatInt(n)} ${n === 1 ? 'request' : 'requests'}; fresh about ${formatMoney(c.fresh)}`;
}

/** What Orgus recommends when it is not to continue, or null: "Starting fresh saves about $4.78". */
export function continuationAdvice(c: Pick<ContinuationCost, 'recommended' | 'saving' | 'forced'>): string | null {
  if (c.recommended === 'continue') return null;
  if (c.saving <= 0) return c.forced === 'compaction' ? 'Start fresh before it compacts' : 'Start fresh past the quality limit';
  return `${c.recommended === 'compact' ? 'Compacting now' : 'Starting fresh'} saves about ${formatMoney(c.saving)}`;
}

/** The tooltip: what the figures are, and how sure they are. */
export function continuationHint(c: Pick<ContinuationCost, 'compact' | 'basis'>): string {
  const from = c.basis === 'measured' ? "from this session's requests since its last progress" : 'from a typical session, until this one has requests to measure';
  return `Expected cost of the rest of the work, ${from}. Fresh means a new session from a handoff; compacting now costs about ${formatMoney(c.compact)}.`;
}

/** The comparison for the drawer, or null without usage, model or prices. */
export function sessionContinuation(s: Pick<Session, 'model'>, u: SessionLiveUsage | null, prices: readonly ModelPrice[] | undefined, now: number): ContinuationCost | null {
  if (!prices?.length) return null;
  return continuationForUsage(u, s.model, (key) => ratesForKey(key, prices), now, { qualityPercent: CONTINUATION_DEFAULTS.qualityPercent });
}

/** What the offer adds beside the line: the action when the handoff is ready, else where it stands. */
export function offerState(offer: ContinuationOffer | null, role: string): 'action' | 'writing' | null {
  if (!offer?.missionId || role === 'viewer') return null;
  return offer.handoff === 'ready' ? 'action' : offer.handoff === 'writing' ? 'writing' : null;
}

function ContinuationLine({ s, c }: { s: Session; c: ContinuationCost }) {
  const advice = continuationAdvice(c);
  const me = useMe();
  const { offer, error: offerError } = useContinuationOffer(s, c);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const state = done ? null : offerState(offer, me.role);
  const go = async () => {
    if (!offer) return;
    setBusy(true);
    setFailed(null);
    try {
      await continueFresh(offer);
      setDone(true);
      toast.success('Continued in a fresh session', 'It starts from the handoff in the same folder and branch.');
    } catch (e) {
      setFailed(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <Tooltip content={continuationHint(c)}>
          <p tabIndex={0} className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-caption text-fg-subtle">
            <span className="num">{continuationText(c)}</span>
            {advice && <span className="num font-medium text-fg-muted">{advice}</span>}
          </p>
        </Tooltip>
        {state === 'action' && (
          <Button size="sm" variant="secondary" icon={<RotateCcw />} loading={busy} onClick={() => void go()}>
            Continue fresh
          </Button>
        )}
        {state === 'writing' && <span className="text-caption text-fg-subtle">Writing the handoff</span>}
        {done && <span className="text-caption text-fg-muted">Continued in a fresh session</span>}
      </div>
      {(failed || offerError) && <ErrorState variant="inline" error={failed ?? offerError} />}
    </div>
  );
}

export function UsageGroup({ s }: { s: Session }) {
  const now = useNow(15_000);
  const cost = useSessionCost(s.id);
  const prices = useCosts()?.pricing?.prices;
  if (agentKindOf(s) !== 'claude-code') return null;
  const u = sessionLiveUsage(s);
  const hit = cacheHitRate(s.tokens);
  const saved = cacheSavedUsd(s.usage?.byModel ?? cost?.byModel, prices);
  const warmth = cacheWarmth(u, now);
  const next = sessionContinuation(s, u, prices, now);
  return (
    <section aria-label="Usage" className="flex flex-col gap-2 border-t border-border-subtle pt-4">
      <h3 className="flex h-control-sm items-center text-caption font-medium text-fg-muted">Usage</h3>
      {!u ? (
        <p className="text-ui text-fg-subtle">Context and cache figures show after its next reply</p>
      ) : (
        <FactGrid
          label="Usage"
          lines={[
            [
              {
                label: 'Context',
                value: (
                  <>
                    <span className="shrink-0">{contextText(u.contextTokens, u.contextWindow)}</span>
                    {u.contextWindow ? (
                      <Gauge value={u.contextTokens / u.contextWindow} warn={CONTEXT_WARN} danger={2} label="Context window used" showValue={false} className="w-12 shrink-0" />
                    ) : (
                      <Tooltip content={`Orgus does not know the context window of ${s.model ?? 'this model'}, so it shows no percent.`}>
                        <span tabIndex={0} className="text-fg-subtle">
                          Window unknown
                        </span>
                      </Tooltip>
                    )}
                  </>
                ),
              },
              {
                label: 'Prompt cache',
                value:
                  hit === null ? (
                    'No input yet'
                  ) : (
                    <Tooltip content={`Share of input read from the cache${saved !== null ? ', and what those reads saved against uncached input at list price' : ''}.${s.partial ? ` ${PARTIAL_HINT}` : ''}`}>
                      <span tabIndex={0} className="min-w-0 truncate">
                        Hit rate {formatPercent(hit)}
                        {saved !== null && saved > 0 && `, saved ${formatMoney(saved)}`}
                      </span>
                    </Tooltip>
                  ),
              },
              {
                label: 'Cache warmth',
                value: (
                  <Tooltip content={u.cacheTtlMs > 5 * MIN ? 'This session writes to the 1 hour cache.' : 'Claude Code keeps the cache 5 minutes after each request.'}>
                    <span tabIndex={0} className="inline-flex min-w-0 items-center gap-1">
                      {warmth.state === 'warm' ? <Flame className="size-3.5 shrink-0 text-fg-muted" aria-hidden /> : <Snowflake className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />}
                      <span className="min-w-0 truncate">{warmthText(warmth)}</span>
                    </span>
                  </Tooltip>
                ),
              },
            ],
          ]}
        />
      )}
      {next && <ContinuationLine s={s} c={next} />}
    </section>
  );
}
